import { SchoolEnrollment } from './../model/school-enrollment.model';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { Region } from 'src/app/model/region.model';
import { Student } from 'src/app/model/student/student.model';
import { BaseRestApiService } from 'src/app/service/baseRestApiService.service';
import { UrlProviderService, EndPoints } from 'src/app/service/urlprovider.service';
import { SchoolEnrollmentDetails } from '../model/school-enrollment-details.model';

@Injectable({
    providedIn: 'root'
})
export class StudentSchoolEnrollmentService extends BaseRestApiService {
    constructor(http: HttpClient, urlProvider: UrlProviderService) {
        super(http, urlProvider);
    }

    getSchoolEnrollments(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.studentSchoolEnrollment_GetSchoolEnrollments, '', studentId);
    }

    getProvisionalSchoolEnrollments(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.studentSchoolEnrollment_GetProvisionalSchoolEnrollments, '', studentId);
    }

    getSchoolEnrollmentDetailTypes(): Observable<any> {
        return this.get<any>(EndPoints.studentSchoolEnrollment_GetSchoolEnrollmentDetailTypes, undefined, undefined);
    }

    getNextSchoolEnrollment(currentEnrollmentDate: string, currentRegion: Region, students: Student[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_GetNextSchoolEnrollment, false, null, {
            currentEnrollmentDate: currentEnrollmentDate,
            currentRegion: currentRegion,
            students: students
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getSchoolEnrollmentDetails(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.studentSchoolEnrollment_GetSchoolEnrollmentDetails, '', studentId);
    }

    getSchoolEnrollmentComments(studentId: number): Observable<any> {
        return this.get<any>(EndPoints.studentSchoolEnrollment_GetSchoolEnrollmentComments, '', studentId);
    }

    createV2(students: Student[], schoolEnrollment: SchoolEnrollment): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_CreateV2, false, null, {
            students: students,
            schoolEnrollment: schoolEnrollment
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    update(schoolEnrollment: SchoolEnrollment): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_Update, false, null, {
            schoolEnrollment: schoolEnrollment
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getServicesForSchoolEnrollment(schoolEnrollments: SchoolEnrollment[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_GetServicesForSchoolEnrollment, false, null, {
            schoolEnrollments: schoolEnrollments
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getOtherSchoolsInSameSchoolYear(schoolEnrollments: SchoolEnrollment[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_GetOtherSchoolsInSameSchoolYear, false, null, {
            schoolEnrollments: schoolEnrollments
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    getSchoolEnrollmentCoes(schoolEnrollments: SchoolEnrollment[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_GetSchoolEnrollmentCoes, false, null, {
            schoolEnrollments: schoolEnrollments
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    deleteSchoolEnrollment(schoolEnrollments: SchoolEnrollment[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_Delete, false, null, {
            schoolEnrollments: schoolEnrollments
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    updateMultiple(schoolEnrollments: SchoolEnrollment[], schoolEnrollmentDetails: SchoolEnrollmentDetails[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_UpdateMultiple, false, null, {
            schoolEnrollments: schoolEnrollments,
            schoolEnrollmentDetails: schoolEnrollmentDetails
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    promote(schools: any[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_Promote, false, null, {
            schools: schools
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }

    graduate(graduationDate: Date, students: any[]): Observable<any> {
        return this.post<any>(EndPoints.studentSchoolEnrollment_Graduate, false, null, {
            graduationDate: graduationDate,
            students: students
        }).pipe(
            switchMap((result) => {
                return of(result);
            }),
            catchError((err) => {
                return throwError(() => new Error(err));
            })
        );
    }
}
